/* eslint-disable @typescript-eslint/no-unused-vars, prefer-const, react-hooks/exhaustive-deps */
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBurger as faBurgerSolid,
  faCalendar as faCalendarSolid,
  faComment as faCommentSolid,
  faDollarSign as faDollarSignSolid,
  faFamily as faFamilySolid,
  faHouse as faHouseSolid,
  faSyringe as faSyringeSolid,
  faWrench as faWrenchSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { faBurger, faCalendar, faComment, faDollarSign, faFamily, faHouse, faSyringe, faWrench } from "@fortawesome/pro-thin-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, ButtonBase, Menu, MenuItem, Stack, Typography, useMediaQuery } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, useTheme } from "@mui/material/styles";
import WhiteTreeLogo from "assets/images/white_tree_new.png";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { logout, useAccessToken } from "../app/auth";

export const DRAWER_WIDTH = 250;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  justifyContent: "flex-end",
}));

const Logo = styled("img")(({ theme }) => ({
  width: 84,
  margin: 20,
}));

function NavButton(props: { icon: IconProp; iconSelected: IconProp; text: string; path: string; closeDrawer: () => void }) {
  const { icon, iconSelected, text, path } = props;
  const navigate = useNavigate();

  const buttonPath = path.toLowerCase().trim();
  const windowPath = window.location.pathname?.toLowerCase().trim();

  const isHomePageButton = buttonPath === "/";
  let isSelected = isHomePageButton ? windowPath === "/" : windowPath.startsWith(buttonPath);

  const handleNavigate = () => {
    props.closeDrawer();
    navigate(path);
  };

  return (
    <ListItem key={text} disablePadding sx={{ mx: 1, width: "unset" }} onClick={handleNavigate}>
      <ListItemButton sx={{ borderRadius: 2, backgroundColor: isSelected === true ? "#488241" : "unset" }}>
        <ListItemIcon sx={{ color: "#ffffff" }}>
          <FontAwesomeIcon icon={isSelected ? iconSelected : icon} />
        </ListItemIcon>
        <ListItemText sx={{ color: "#ffffff" }} primary={text} />
      </ListItemButton>
    </ListItem>
  );
}

function UserButton() {
  const accessToken = useAccessToken();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    logout();
  };
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  return (
    <>
      <ButtonBase sx={{ display: "flex", justifyContent: "flex-start", p: 2, gap: 1 }} onClick={handleOpenMenu}>
        <Avatar alt={accessToken?.name} src={accessToken?.picture} sx={{ width: 32, height: 32 }} />
        <Stack>
          <Typography variant="body1" textAlign="left">
            {accessToken?.name}
          </Typography>
          <Typography variant="body2" textAlign="left">
            {accessToken?.email}
          </Typography>
        </Stack>
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={handleLogout}>Sign Out</MenuItem>
      </Menu>
    </>
  );
}

export function AppDrawer(props: { open: boolean; onOpen: () => void; onClose: () => void }) {
  const theme = useTheme();
  const { open, onOpen, onClose } = props;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!isSmallScreen && !open) {
      onOpen();
    } else if (isSmallScreen && open) {
      onClose();
    }
  }, [isSmallScreen]);

  return (
    <Drawer
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        zIndex: 1500,
        "& .MuiBackdrop-root": {
          zIndex: 1499,
        },
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          backgroundColor: (theme) => theme.palette.primary.main,
          zIndex: 1500,
        },
        pointerEvents: open ? "inherit" : "none",
      }}
      variant={isSmallScreen ? "temporary" : "permanent"}
      anchor="left"
      open={open}
      onClose={onClose}
    >
      <DrawerHeader sx={!isSmallScreen ? theme.mixins.toolbar : { minHeight: 0 }}>
        <Stack alignItems="center" sx={{ width: "100%" }}>
          <Logo src={WhiteTreeLogo} />
        </Stack>
      </DrawerHeader>
      <List sx={{ "& li": { marginBottom: 1 } }}>
        <NavButton text="Home" path="/" icon={faHouse} iconSelected={faHouseSolid} closeDrawer={onClose} />
        <NavButton text="Families" path="/families" icon={faFamily} iconSelected={faFamilySolid} closeDrawer={onClose} />
        <NavButton text="Bookkeeping" path="/bookkeeping" icon={faDollarSign} iconSelected={faDollarSignSolid} closeDrawer={onClose} />
        <NavButton text="Communications" path="/communications" icon={faComment} iconSelected={faCommentSolid} closeDrawer={onClose} />
        <NavButton text="Calendar" path="/calendar" icon={faCalendar} iconSelected={faCalendarSolid} closeDrawer={onClose} />
        <NavButton text="Tools" path="/tools" icon={faWrench} iconSelected={faWrenchSolid} closeDrawer={onClose} />
        <NavButton text="Immunizations" path="/immunizations" icon={faSyringe} iconSelected={faSyringeSolid} closeDrawer={onClose} />
        <NavButton text="Daily Reports" path="/daily-reports" icon={faBurger} iconSelected={faBurgerSolid} closeDrawer={onClose} />
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <UserButton />
    </Drawer>
  );
}
